import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Form, Input, Button, Select, message, Spin, Empty
} from "antd";
import { 
  RightOutlined, PlusOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import AdminAssessmentReportDisplay from "./AdminAssessmentReportDisplay";

const { TextArea } = Input;

const TECHNOLOGY_OPTIONS = {
  Frontend: ["React", "Angular", "Vue", "Next.js"],
  Backend: ["Node.js", "Java", "Python", ".NET", "Go"],
  Mobile: ["Flutter", "React Native", "Swift", "Kotlin"],
  Database: ["MySQL", "PostgreSQL", "MongoDB", "Redis", "Firebase"],
  "Cloud / Infra": ["AWS", "Azure", "GCP", "Docker", "Kubernetes"],
  "Data / AI": ["Snowflake", "BigQuery", "Databricks", "OpenAI", "LangChain"]
};

const ALL_TECHNOLOGIES = Object.values(TECHNOLOGY_OPTIONS).flat();

const SECTION_TITLES = [
  "Project Context & Objectives",
  "Technical Environment",
  "Capabilities & Challenges",
  "Preferences & Next Steps"
];

const SURVEY_SECTIONS = [
  {
    title: "Project Context & Objectives",
    questions: [
      {
        id: "projectType",
        question: "What best describes this project?",
        type: "single",
        options: [
          { value: "new_product", label: "New product or platform" },
          { value: "modernizing", label: "Modernizing an existing system" },
          { value: "integration", label: "Integrating tools or APIs" },
          { value: "migration", label: "Cloud or infrastructure migration" },
          { value: "continuous_improvement", label: "Continuous improvement of an existing system" }
        ]
      },
      {
        id: "solutionFocus",
        question: "Which area does this project primarily focus on?",
        type: "single",
        options: [
          { value: "web_app", label: "Web application" },
          { value: "mobile_app", label: "Mobile application" },
          { value: "data_analytics", label: "Data & analytics" },
          { value: "cloud_devops", label: "Cloud / DevOps platform" },
          { value: "ai_automation", label: "AI or automation system" }
        ]
      },
      {
        id: "scaleScope",
        question: "What stage is this project currently in?",
        type: "single",
        options: [
          { value: "mvp", label: "MVP or pilot" },
          { value: "medium", label: "Medium enterprise solution" },
          { value: "full_scale", label: "Full-scale platform" },
          { value: "ongoing", label: "Ongoing evolution of an existing system" }
        ]
      },
      {
        id: "technicalMaturity",
        question: "What is the expected scale or scope?",
        type: "single",
        options: [
          { value: "minimal", label: "Small / Minimal scope" },
          { value: "basic", label: "Medium / Standard scope" },
          { value: "moderate", label: "Large / Enterprise scope" },
          { value: "cloud_native", label: "Enterprise-wide transformation" }
        ]
      },
      {
        id: "businessOutcome",
        question: "What outcome is the customer aiming to achieve?",
        type: "single",
        options: [
          { value: "time_to_market", label: "Faster time-to-market" },
          { value: "performance", label: "Performance & reliability" },
          { value: "cost_optimization", label: "Cost optimization" },
          { value: "user_experience", label: "Better user experience" },
          { value: "innovation", label: "New capabilities or innovation" }
        ]
      }
    ]
  },
  {
    title: "Technical Environment",
    questions: [
      {
        id: "currentMaturity",
        question: "How mature is the customer's current technical setup?",
        type: "single",
        options: [
          { value: "minimal", label: "Minimal or undefined" },
          { value: "basic", label: "Basic stack, limited automation" },
          { value: "moderate", label: "Moderately modern with partial DevOps" },
          { value: "cloud_native", label: "Cloud-native with automated pipelines" }
        ]
      },
      {
        id: "internalCapability",
        question: "What internal capability does the customer currently have?",
        type: "single",
        options: [
          { value: "no_team", label: "No internal engineering team" },
          { value: "small_team", label: "Small team needing strong guidance" },
          { value: "capable_team", label: "Capable team needing extra capacity" },
          { value: "strong_team", label: "Strong team needing niche expertise" }
        ]
      },
      {
        id: "integrationComplexity",
        question: "How complex are the integration requirements?",
        type: "single",
        options: [
          { value: "simple", label: "Simple - Few external systems" },
          { value: "moderate", label: "Moderate - Several APIs and services" },
          { value: "complex", label: "Complex - Many legacy and modern systems" },
          { value: "enterprise", label: "Enterprise - Critical system integrations" }
        ]
      },
      {
        id: "dataSensitivity",
        question: "What level of data sensitivity applies?",
        type: "single",
        options: [
          { value: "low", label: "Low - Public or non-sensitive data" },
          { value: "medium", label: "Medium - Business confidential" },
          { value: "high", label: "High - PII or regulated data" },
          { value: "critical", label: "Critical - Financial or healthcare data" }
        ]
      }
    ]
  },
  {
    title: "Capabilities & Challenges",
    questions: [
      {
        id: "keyChallenges",
        question: "What are the customer's biggest challenges?",
        type: "multiple",
        options: [
          { value: "skill_gaps", label: "Skill gaps" },
          { value: "limited_capacity", label: "Limited engineering capacity" },
          { value: "slow_delivery", label: "Slow delivery cycles" },
          { value: "lack_leadership", label: "Lack of architecture or DevOps leadership" },
          { value: "product_clarity", label: "Product ownership or clarity gaps" }
        ]
      },
      {
        id: "futureDirection",
        question: "Which direction is the customer moving toward?",
        type: "multiple",
        options: [
          { value: "cloud_native", label: "Cloud-native & scalable" },
          { value: "high_availability", label: "High availability & resilience" },
          { value: "performance", label: "Performance & latency optimization" },
          { value: "data_ai", label: "Data-driven or AI-enabled systems" }
        ]
      },
      {
        id: "timeline",
        question: "What is the expected timeline?",
        type: "single",
        options: [
          { value: "urgent", label: "Urgent - Within 1-2 months" },
          { value: "short", label: "Short term - 3-6 months" },
          { value: "medium", label: "Medium term - 6-12 months" },
          { value: "long", label: "Long term - 12+ months" }
        ]
      }
    ]
  },
  {
    title: "Preferences & Next Steps",
    questions: [
      {
        id: "engagementPreference",
        question: "What kind of external support does the customer expect?",
        type: "single",
        options: [
          { value: "individual", label: "Individual specialists" },
          { value: "dedicated_team", label: "Dedicated team" },
          { value: "hybrid", label: "Hybrid (core team + contractors)" },
          { value: "managed", label: "Managed delivery" }
        ]
      },
      {
        id: "costQualityBalance",
        question: "How should cost and quality be balanced?",
        type: "single",
        options: [
          { value: "cost_sensitive", label: "Cost-sensitive" },
          { value: "balanced", label: "Balanced" },
          { value: "quality_first", label: "Quality-first" },
          { value: "need_recommendation", label: "Need recommendation" }
        ]
      },
      {
        id: "decisionAuthority",
        question: "Who will be making the final decision?",
        type: "single",
        options: [
          { value: "self", label: "Customer is the decision maker" },
          { value: "team", label: "Customer's team / department" },
          { value: "executive", label: "Executive leadership" },
          { value: "committee", label: "Committee / board approval required" }
        ]
      }
    ]
  }
];

const AssessmentWrapper = styled.div`
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  padding: 60px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .assessment-card {
    max-width: 1100px;
    width: 100%;
    margin: 0 auto;
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    height: calc(100vh - 120px);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 40px;
    border-bottom: 1px solid #e8e8e8;
    flex-shrink: 0;

    .logo {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 20px;
      font-weight: 700;
      color: #1a1a1a;

      .logo-icon {
        width: 28px;
        height: 28px;
        background: #1a1a1a;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
      }

      span {
        font-weight: 800;
      }
    }

    .header-title {
      font-size: 14px;
      color: #595959;
      font-weight: 500;
    }

    .admin-badge {
      background: linear-gradient(135deg, #014c75 0%, #0277a8 100%);
      color: white;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      margin-left: 12px;
    }
  }

  .card-content {
    padding: 40px 56px 48px;
    flex: 1;
    overflow-y: auto;

    @media (max-width: 768px) {
      padding: 32px 24px 40px;
    }
  }

  .questions-list {
    padding-bottom: 24px;
  }

  .details-page {
    padding-bottom: 24px;
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 40px;
    border-top: 1px solid #e8e8e8;
    background: #fafafa;
    border-radius: 0 0 16px 16px;
    flex-shrink: 0;

    .footer-left {
      display: flex;
      gap: 12px;
    }

    .footer-right {
      display: flex;
      gap: 12px;
    }
  }

  .welcome-content {
    h1 {
      font-size: 24px;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 24px;
      line-height: 1.4;
    }

    .description {
      font-size: 15px;
      color: #595959;
      line-height: 1.7;
      margin-bottom: 32px;
      max-width: 700px;
    }

    .privacy-note {
      font-size: 13px;
      color: #8c8c8c;
      line-height: 1.6;
      padding: 16px;
      background: rgba(1, 76, 117, 0.05);
      border-radius: 8px;
      border-left: 3px solid #014c75;

      strong {
        color: #014c75;
      }
    }
  }

  .progress-bar {
    display: flex;
    align-items: center;
    margin-bottom: 40px;
    padding: 0 20px;

    .progress-line {
      flex: 1;
      height: 4px;
      background: #e0e0e0;
      position: relative;
      margin: 0 -6px;

      .progress-fill {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background: #01d9a9;
        transition: width 0.3s ease;
      }
    }

    .progress-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #e0e0e0;
      border: 2px solid #e0e0e0;
      position: relative;
      z-index: 1;
      transition: all 0.3s ease;

      &.active {
        background: #01d9a9;
        border-color: #01d9a9;
        transform: scale(1.2);
      }

      &.completed {
        background: #01d9a9;
        border-color: #01d9a9;
      }
    }
  }

  .section-title {
    font-size: 22px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 32px;
  }

  .questions-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .question-row {
    .question-label {
      font-size: 15px;
      font-weight: 500;
      color: #1a1a1a;
      margin-bottom: 10px;
    }

    .ant-select {
      width: 100%;
    }

    .ant-select-selector {
      height: 44px !important;
      border-radius: 6px !important;
      border-color: #d9d9d9 !important;
      padding: 6px 12px !important;

      .ant-select-selection-item,
      .ant-select-selection-placeholder {
        line-height: 30px !important;
        font-size: 14px;
      }
    }

    .multi-select-options {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 8px;

      .option-chip {
        padding: 8px 16px;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        font-size: 14px;
        color: #595959;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          border-color: #01d9a9;
          background: #f8fffd;
        }

        &.selected {
          border-color: #01d9a9;
          background: rgba(1, 217, 169, 0.1);
          color: #00a085;
          font-weight: 500;
        }
      }
    }
  }

  .details-page {
    .section-label {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 8px;
    }

    .section-helper {
      font-size: 13px;
      color: #8c8c8c;
      margin-bottom: 12px;
    }

    .field-group {
      margin-bottom: 28px;
    }

    .tech-input-row {
      display: flex;
      gap: 8px;
      margin-top: 12px;
    }
  }

  .btn-back {
    height: 44px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    background: white;

    &:hover {
      border-color: #b3b3b3;
    }
  }

  .btn-next {
    height: 44px;
    padding: 0 28px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
    background: #01d9a9;
    border: none;
    color: white;

    &:hover {
      background: #00c49a;
    }

    &:disabled {
      background: #e0e0e0;
      color: #999;
    }
  }
`;

const AdminAIProjectAssessment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedEngagementId = searchParams.get("engagementId");
  const preselectedCustomerId = searchParams.get("customerId");
  
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customerOrganizations, setCustomerOrganizations] = useState([]);
  const [allEngagements, setAllEngagements] = useState([]);
  const [filteredEngagements, setFilteredEngagements] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customTech, setCustomTech] = useState("");
  const [selectedTechnologies, setSelectedTechnologies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [step1FormData, setStep1FormData] = useState({ 
    customerOrganization: null,
    engagementId: null, 
    requirementDescription: '', 
    technologies: [] 
  });

  const TOTAL_PAGES = 6;

  useEffect(() => {
    fetchCustomerOrganizations();
    fetchAllActiveEngagements();
    if (preselectedCustomerId) {
      setSelectedCustomer(preselectedCustomerId);
      form.setFieldsValue({ customerOrganization: preselectedCustomerId });
    }
  }, []);

  useEffect(() => {
    if (preselectedEngagementId && allEngagements.length > 0) {
      form.setFieldsValue({ engagementId: preselectedEngagementId });
    }
  }, [preselectedEngagementId, allEngagements]);

  useEffect(() => {
    if (selectedCustomer) {
      const filtered = allEngagements.filter(
        (eng) => eng.customerOrganization === selectedCustomer
      );
      setFilteredEngagements(filtered);
    } else {
      setFilteredEngagements([]);
    }
  }, [selectedCustomer, allEngagements]);

  const fetchCustomerOrganizations = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.GET_CUSTOMER_ORGANIZATIONS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomerOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      message.error("Failed to load customer organizations");
    }
  };

  const fetchAllActiveEngagements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ENGAGEMENTS,
        { status: "active" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllEngagements(response.data.engagements || []);
    } catch (error) {
      console.error("Error fetching engagements:", error);
      message.error("Failed to load engagements");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (value) => {
    setSelectedCustomer(value);
    form.setFieldValue("engagementId", undefined);
  };

  const handleAddCustomTech = () => {
    if (customTech.trim() && !selectedTechnologies.includes(customTech.trim())) {
      setSelectedTechnologies([...selectedTechnologies, customTech.trim()]);
      setCustomTech("");
    }
  };

  const handleTechChange = (values) => {
    setSelectedTechnologies(values);
  };

  const handleSurveyAnswer = (questionId, value, isMultiple) => {
    if (isMultiple) {
      const currentAnswers = surveyAnswers[questionId] || [];
      const newAnswers = currentAnswers.includes(value)
        ? currentAnswers.filter(v => v !== value)
        : [...currentAnswers, value];
      setSurveyAnswers({ ...surveyAnswers, [questionId]: newAnswers });
    } else {
      setSurveyAnswers({ ...surveyAnswers, [questionId]: value });
    }
  };

  const validateDetailsPage = async () => {
    try {
      await form.validateFields(['customerOrganization', 'engagementId', 'requirementDescription']);
      return true;
    } catch {
      return false;
    }
  };

  const handleNext = async () => {
    if (currentPage === 1) {
      const isValid = await validateDetailsPage();
      if (!isValid) return;
      
      const values = form.getFieldsValue();
      setStep1FormData({
        customerOrganization: values.customerOrganization,
        engagementId: values.engagementId,
        requirementDescription: values.requirementDescription,
        technologies: selectedTechnologies
      });
    }
    
    if (currentPage < TOTAL_PAGES - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleBack = () => {
    if (currentPage === 0) {
      navigate("/admin/projects/create");
    } else {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      
      const assessmentData = {
        customerOrganization: step1FormData.customerOrganization,
        engagementId: step1FormData.engagementId,
        requirementDescription: step1FormData.requirementDescription,
        technologies: step1FormData.technologies || [],
        surveyResponses: surveyAnswers,
        createdByAdmin: true
      };

      const response = await axios.post(
        API_CONST.CREATE_AI_PROJECT_ASSESSMENT,
        assessmentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.isExisting) {
        message.info("Found existing assessment with matching criteria");
      } else {
        message.success("Assessment generated successfully!");
      }
      
      setAssessmentResult(response.data.assessment);
      setCurrentPage(TOTAL_PAGES);
    } catch (error) {
      console.error("Submission failed:", error);
      message.error(error.response?.data?.error || "Failed to submit assessment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateJobFromAssessment = () => {
    if (assessmentResult?.engagementId) {
      navigate(`/admin/job-requirments/create?engagementId=${assessmentResult.engagementId}`);
    } else {
      navigate("/admin/job-requirments/create");
    }
  };

  if (currentPage === TOTAL_PAGES && assessmentResult) {
    return (
      <AdminAssessmentReportDisplay
        assessment={assessmentResult}
        onBack={() => navigate("/admin/projects")}
        onCreateJob={handleCreateJobFromAssessment}
        onViewAssessments={() => navigate("/admin/assessments")}
        onNavigate={(path) => navigate(path)}
      />
    );
  }

  const renderHeader = () => (
    <div className="card-header">
      <div className="logo">
        <div className="logo-icon">H</div>
        <span>Hire<span style={{ fontWeight: 400 }}>NXT</span></span>
        <span className="admin-badge">Admin</span>
      </div>
      <div className="header-title">Build Technical Roadmap - Assessment (on behalf of customer)</div>
    </div>
  );

  const renderProgressBar = () => {
    const surveyStartPage = 2;
    const totalSections = SURVEY_SECTIONS.length;
    const currentSection = currentPage - surveyStartPage;

    return (
      <div className="progress-bar">
        {SURVEY_SECTIONS.map((_, idx) => (
          <React.Fragment key={idx}>
            <div 
              className={`progress-dot ${idx < currentSection ? 'completed' : ''} ${idx === currentSection ? 'active' : ''}`}
            />
            {idx < totalSections - 1 && (
              <div className="progress-line">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: idx < currentSection ? '100%' : (idx === currentSection ? '50%' : '0%')
                  }} 
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  if (currentPage === 0) {
    return (
      <AssessmentWrapper>
        <div className="assessment-card">
          {renderHeader()}
          <div className="card-content">
            <div className="welcome-content">
              <h1>Create Assessment on Behalf of Customer</h1>
              <p className="description">
                As an admin, you can complete this assessment for a customer. 
                Answer the questionnaire based on the customer's requirements to get a downloadable Project Blueprint, 
                role-by-role staffing recommendations, and a contract vs full-time cost comparison.
              </p>
              <p className="privacy-note">
                <strong>Admin Note:</strong> This assessment will be created under the selected customer's organization 
                and will be visible in their assessments list. All data will be attributed to the customer.
              </p>
            </div>
          </div>
          <div className="card-footer">
            <div className="footer-left">
              <Button className="btn-back" onClick={() => navigate("/admin/projects/create")}>
                Cancel
              </Button>
            </div>
            <div className="footer-right">
              <Button 
                type="primary" 
                className="btn-next"
                onClick={() => setCurrentPage(1)}
              >
                Start Assessment
              </Button>
            </div>
          </div>
        </div>
      </AssessmentWrapper>
    );
  }

  if (currentPage === 1) {
    return (
      <AssessmentWrapper>
        <div className="assessment-card">
          {renderHeader()}
          <div className="card-content details-page">
            <h2 className="section-title">Customer & Project Details</h2>
            
            <Form form={form} layout="vertical">
              <div className="field-group">
                <div className="section-label">Select Customer Organization *</div>
                <div className="section-helper">Choose the customer this assessment is for</div>
                <Form.Item
                  name="customerOrganization"
                  rules={[{ required: true, message: "Please select a customer organization" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Select customer organization"
                    size="large"
                    showSearch
                    optionFilterProp="children"
                    onChange={handleCustomerChange}
                    loading={loading}
                    notFoundContent={loading ? <Spin size="small" /> : <Empty description="No customer organizations" />}
                  >
                    {customerOrganizations.map((org) => (
                      <Select.Option key={org.id} value={org.id}>
                        {org.companyName || org.name || org.id}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field-group">
                <div className="section-label">Select Engagement *</div>
                <div className="section-helper">Choose the engagement this project belongs to</div>
                <Form.Item
                  name="engagementId"
                  rules={[{ required: true, message: "Please select an engagement" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder={selectedCustomer ? "Select an engagement" : "Select customer first"}
                    size="large"
                    showSearch
                    optionFilterProp="children"
                    disabled={!selectedCustomer}
                    notFoundContent={<Empty description="No active engagements for this customer" />}
                  >
                    {filteredEngagements.map((eng) => (
                      <Select.Option key={eng.id} value={eng.id}>
                        {eng.title}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field-group">
                <div className="section-label">Technologies (Optional)</div>
                <div className="section-helper">Select or add technologies the customer plans to use</div>
                <Select
                  mode="multiple"
                  placeholder="Select technologies"
                  size="large"
                  style={{ width: "100%" }}
                  value={selectedTechnologies}
                  onChange={handleTechChange}
                  optionFilterProp="children"
                >
                  {Object.entries(TECHNOLOGY_OPTIONS).map(([category, techs]) => (
                    <Select.OptGroup key={category} label={category}>
                      {techs.map((tech) => (
                        <Select.Option key={tech} value={tech}>
                          {tech}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  ))}
                  {selectedTechnologies
                    .filter((tech) => !ALL_TECHNOLOGIES.includes(tech))
                    .map((customTech) => (
                      <Select.Option key={customTech} value={customTech}>
                        {customTech} (Custom)
                      </Select.Option>
                    ))}
                </Select>
                <div className="tech-input-row">
                  <Input
                    placeholder="Add custom technology..."
                    value={customTech}
                    onChange={(e) => setCustomTech(e.target.value)}
                    onPressEnter={handleAddCustomTech}
                  />
                  <Button 
                    icon={<PlusOutlined />} 
                    onClick={handleAddCustomTech}
                    disabled={!customTech.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="field-group">
                <div className="section-label">Requirement Description *</div>
                <div className="section-helper">
                  Describe what the customer is building or transforming. Include business goals, 
                  current challenges, and what success looks like.
                </div>
                <Form.Item
                  name="requirementDescription"
                  rules={[
                    { required: true, message: "Please provide a project description" },
                    { min: 50, message: "Please provide at least 50 characters" }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    placeholder="Describe the customer's requirements..."
                    rows={5}
                    maxLength={2000}
                    showCount
                  />
                </Form.Item>
              </div>
            </Form>
          </div>
          <div className="card-footer">
            <div className="footer-left">
              <Button className="btn-back" onClick={() => navigate("/admin/projects/create")}>
                Cancel
              </Button>
            </div>
            <div className="footer-right">
              <Button className="btn-back" onClick={handleBack}>Back</Button>
              <Button type="primary" className="btn-next" onClick={handleNext}>
                Next
              </Button>
            </div>
          </div>
        </div>
      </AssessmentWrapper>
    );
  }

  const sectionIndex = currentPage - 2;
  const currentSection = SURVEY_SECTIONS[sectionIndex];
  const isLastSection = sectionIndex === SURVEY_SECTIONS.length - 1;

  return (
    <AssessmentWrapper>
      <div className="assessment-card">
        {renderHeader()}
        <div className="card-content">
          {renderProgressBar()}
          
          <h2 className="section-title">{currentSection.title}</h2>
          
          <div className="questions-list">
            {currentSection.questions.map((q, idx) => (
              <div key={q.id} className="question-row">
                <div className="question-label">
                  Q{idx + 1}. {q.question} {q.type === 'single' ? '(single)' : '(multiple)'}
                </div>
                
                {q.type === 'single' ? (
                  <Select
                    placeholder="Select an option"
                    size="large"
                    value={surveyAnswers[q.id]}
                    onChange={(value) => handleSurveyAnswer(q.id, value, false)}
                  >
                    {q.options.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <div className="multi-select-options">
                    {q.options.map((option) => {
                      const isSelected = (surveyAnswers[q.id] || []).includes(option.value);
                      return (
                        <div
                          key={option.value}
                          className={`option-chip ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSurveyAnswer(q.id, option.value, true)}
                        >
                          {option.label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="card-footer">
          <div className="footer-left">
            <Button className="btn-back" onClick={() => navigate("/admin/projects/create")}>
              Cancel
            </Button>
          </div>
          <div className="footer-right">
            <Button className="btn-back" onClick={handleBack}>Back</Button>
            {isLastSection ? (
              <Button 
                type="primary" 
                className="btn-next" 
                onClick={handleSubmit}
                loading={submitting}
              >
                Generate Assessment <RightOutlined />
              </Button>
            ) : (
              <Button type="primary" className="btn-next" onClick={handleNext}>
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </AssessmentWrapper>
  );
};

export default AdminAIProjectAssessment;
